import { useIsMobile } from '@ox-technologies/hooks';
import { LogoLOXLockup } from '@ox-technologies/ui';
import Ellipses, { EllipsesSize } from 'components/public-layout/ellipses';
import IdentityCard from 'components/public-layout/identity-card';
import SceneAnchor from 'components/public-layout/image/image';
import LayoutBackground from 'components/public-layout/layout-background';
import LayoutHeadings from 'components/public-layout/layout-headings';
import { lazy, ReactNode, Suspense, useRef, useState } from 'react';
import { MOBILE_BREAKPOINT } from 'utils/styles/breakpoints';

// Lazy so three.js lands in its own async chunk — the login form paints first
const BoxScene = lazy(
  () => import('components/public-layout/box-scene/box-scene')
);

type LayoutWrapperProps = {
  headingText: string;
  subheadingText: string;
  isLoginScreen: boolean;
  children: ReactNode;
};

/**
 * Public-layout shell: LOX-branded navy panel (logo, welcome headings and the
 * three.js locker scene on the login screen) next to the white identity card.
 * The scene canvas is full-bleed behind the content — its survey grid and
 * radar waves span the whole screen — while the cabinet is framed to the
 * SceneAnchor slot. Collapses to a single column below `md` (768px), where
 * the static Ellipses take over as the decoration.
 */
const LayoutWrapper = ({
  headingText,
  subheadingText,
  isLoginScreen,
  children,
}: LayoutWrapperProps) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [sceneReady, setSceneReady] = useState(false);
  // The scene column is CSS-hidden below `md`, but that alone would still
  // mount three.js and render to an invisible canvas — skip it entirely.
  const isMobile = useIsMobile(MOBILE_BREAKPOINT);

  return (
    <LayoutBackground>
      {isLoginScreen && !isMobile ? (
        <div aria-hidden className="absolute inset-0 z-[745] hidden md:block">
          <Suspense fallback={null}>
            <BoxScene
              anchorRef={anchorRef}
              onReady={() => setSceneReady(true)}
            />
          </Suspense>
        </div>
      ) : null}
      <div className="absolute left-1/2 top-1/2 z-[750] h-[768px] max-h-screen w-[1366px] max-w-[100vw] -translate-x-1/2 -translate-y-1/2 p-[16px] max-md:h-screen">
        <div className="relative grid h-full w-full grid-cols-[auto_440px] justify-items-center max-md:grid-cols-1">
          <div className="relative flex flex-col items-center justify-center">
            <LogoLOXLockup
              module="Identity"
              color="#fff"
              height={30}
              className="mb-[28px]"
            />
            <LayoutHeadings
              heading={headingText}
              subHeading={subheadingText}
              isLoginScreen={isLoginScreen}
            />
            {isLoginScreen ? (
              <SceneAnchor ref={anchorRef} showFallback={!sceneReady} />
            ) : null}
            {/* On the desktop login screen the scene's radar waves replace
                the static ellipses; everywhere else they stay. */}
            <div className={isLoginScreen ? 'md:hidden' : undefined}>
              <Ellipses width={EllipsesSize.small} />
            </div>
          </div>
          <IdentityCard>{children}</IdentityCard>
        </div>
      </div>
    </LayoutBackground>
  );
};

export default LayoutWrapper;
